import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import { UserIntegrationsContext } from "contexts/UserIntegrations";
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { UpgradeDialogSource } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import TemplateEditButton from "components/TemplateEditButton";
import OutreachSelect from "components/TemplateScreen/OutreachSelect";
import { MAXIMUM_FOLLOWUP_ALLOWED } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import { GooglePlaceBrand } from "../schema";
import { PendingItem } from "./PendingItem";
import { BrandStatus, BrandVerificationData } from "./schema";

interface IProps {
  googlePlaceBrands: GooglePlaceBrand[];
  setGooglePlaceBrands: Dispatch<SetStateAction<GooglePlaceBrand[]>>;
  open: boolean;
  searchQuery: string;
  handleClose: () => void;
  handleBack: () => void;
  openCompleted: (campaignId: number) => void;
}

type TemplateError = {
  idx: number;
  message: string;
};

type ConfirmDialogData = {
  header: string;
  subheader: string;
  confirmText: string;
  confirm: () => void;
  cancelText?: string;
  cancel?: () => void;
};

export default function BrandVerificationDialog({
  googlePlaceBrands,
  setGooglePlaceBrands,
  open,
  searchQuery,
  handleClose,
  handleBack,
  openCompleted,
}: IProps) {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { setUpgradeDialogSource } = useContext(SubscriptionContext);
  const { setOpenIntegrationDialog } = useContext(UserIntegrationsContext);

  const {
    templates: allTemplates,
    outreachTemplates,
    followupTemplates,
    fetchOutreachTemplates,
    setCreateTemplateDialogOpen,
  } = useContext(OutreachTemplatesContext);
  const { emailSettings, currentOrg } = useContext(OrganizationUserContext);
  const [templatesError, setTemplatesError] = useState<TemplateError | null>(
    null,
  );
  const [templates, setTemplates] = useState<Template[]>([]);
  const { getToken } = useAuth();
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const [nextLoading, setNextLoading] = useState(false);
  const [brandsError, setBrandsError] = useState("");
  const [brandVerifications, setBrandVerifications] = useState<
    BrandVerificationData[]
  >([]);
  const [confirmDialogData, setConfirmDialogData] =
    useState<ConfirmDialogData | null>(null);

  const selectedFollowups = templates?.slice(1, templates?.length);
  const templateFromEmailSettings = emailSettings?.emailSettingTemplates?.map(
    (template) => template.templateId,
  );
  const filteredOutreachTemplates = outreachTemplates?.filter(
    (t) => !t.emailTemplateId,
  );
  const filteredFollowUps = followupTemplates?.filter(
    (t) => !t.emailTemplateId,
  );

  const handleTemplateChange = (e: CustomEvent, index: number) => {
    const { value } = e.target;
    const copy = [...templates];
    const template = allTemplates?.find((x) => x.id === value);
    if (template) {
      copy[index] = template;
      setTemplates(copy);
    }
  };

  const removeFollowUp = (index: number) => {
    const copy = [...templates];
    copy.splice(index, 1);
    setTemplates(copy);
  };

  const addFollowUp = () => {
    setTemplates([...templates, followupTemplates?.[0]]);
  };

  const initializeEmails = () => {
    if (templates?.length > 0) return;

    if (allTemplates?.length === 0) {
      fetchOutreachTemplates();
      return;
    }

    const presetTemplates = emailSettings?.emailSettingTemplates || [];
    const matchedTemplates = [];

    for (const settingTemplate of presetTemplates) {
      const template = allTemplates?.find(
        (x) => x.id === settingTemplate.templateId,
      );
      if (template) {
        matchedTemplates.push(template);
      }
    }
    if (matchedTemplates?.length > 0) {
      setTemplates(matchedTemplates);
      return;
    }

    if (filteredOutreachTemplates?.length > 0) {
      setTemplates([filteredOutreachTemplates[0]]);
    } else if (filteredOutreachTemplates?.length === 0) {
      fetchOutreachTemplates();
    }
  };

  const handleGenerateTemplate = () => {
    trackEvent("Generate Template Button Clicked", {
      Source: "Local Pitching",
    });
    setCreateTemplateDialogOpen(true);
  };

  useEffect(() => {
    if (templates.length === 0 && emailSettings?.emailSettingTemplates) {
      initializeEmails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSettings?.emailSettingTemplates?.length, allTemplates?.length]);

  useEffect(() => {
    if (open && googlePlaceBrands.length > 0) {
      initializeBrandVerifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializeBrandVerifications = () => {
    const initialData = googlePlaceBrands.map((googlePlaceBrand) => ({
      googlePlaceBrand,
      status: "loading" as BrandStatus,
    }));
    setBrandVerifications(initialData);
    verifyBrandsInParallel(initialData);
  };

  const verifyBrandsInParallel = async (
    initialData: BrandVerificationData[],
  ) => {
    if (!currentOrg?.id) return;

    initialData.forEach(async (brandData, index) => {
      try {
        const { bentoBrand, bentoContacts } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/google-place-brands/${brandData.googlePlaceBrand.id}/bento-brand`,
          "POST",
        );

        const hasContacts = bentoContacts && bentoContacts.length > 0;
        const hasBentoBrand = !!bentoBrand;

        const updatedBrandData = {
          ...brandData,
          status:
            hasContacts && hasBentoBrand
              ? BrandStatus.contactsFound
              : BrandStatus.contactsNotFound,
          bentoBrand,
          bentoContacts,
        };

        setBrandVerifications((prev) =>
          prev.map((brand, i) => (i === index ? updatedBrandData : brand)),
        );
      } catch (error) {
        console.error(
          `Error verifying brand ${brandData.googlePlaceBrand.displayName}:`,
          error,
        );

        const updatedBrandData = {
          ...brandData,
          status: BrandStatus.contactsNotFound,
        };

        setBrandVerifications((prev) =>
          prev.map((brand, i) => (i === index ? updatedBrandData : brand)),
        );
      }
    });
  };

  const removeGooglePlaceBrand = (googlePlaceBrandId: number) => {
    setBrandVerifications((verifications) =>
      verifications.filter((v) => v.googlePlaceBrand.id !== googlePlaceBrandId),
    );
    setGooglePlaceBrands((googlePlaceBrands) =>
      googlePlaceBrands.filter((b) => b.id !== googlePlaceBrandId),
    );
    setBrandsError("");
  };

  const handleCreateCollection = async (overridePending: boolean) => {
    if (!currentOrg?.id) return;

    setNextLoading(true);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/google-place-outreach-collections`,
        "POST",
        {},
        {
          collectionName: searchQuery,
          googlePlaceBrandIds: googlePlaceBrands.map(
            (googlePlace) => googlePlace.id,
          ),
          templateIds: templates
            .map((t) => t.id)
            .filter((id): id is number => id !== undefined),
          overridePending,
        },
        true,
      );

      const jsonMessage = await res.json();

      if (!res.ok) {
        if (jsonMessage.field) {
          if (
            jsonMessage.field === "templates" &&
            jsonMessage.templateIdx !== undefined &&
            jsonMessage.templateIdx !== null
          ) {
            setTemplatesError({
              idx: jsonMessage.templateIdx,
              message: jsonMessage.message,
            });
            return;
          }

          if (jsonMessage.field === "google_place_brand_ids") {
            if (jsonMessage.message.includes("processing")) {
              setConfirmDialogData({
                header: "Still Searching",
                subheader: `We're still looking for contacts for these brands: ${brandVerifications
                  .filter((bV) => bV.status === BrandStatus.loading)
                  .map((bV) => bV.googlePlaceBrand.displayName)
                  .join(
                    ", ",
                  )}. Do you want to continue and exclude these brands from this email campaign?`,
                confirmText: "Yes",
                cancelText: "No",
                cancel: () => {
                  setConfirmDialogData(null);
                },
                confirm: () => {
                  handleCreateCollection(true);
                  setConfirmDialogData(null);
                },
              });
              return;
            } else if (jsonMessage.message.includes("no brands found")) {
              setConfirmDialogData({
                header: "No Contacts to Email",
                subheader: `We couldn't find any new contacts for the brands you selected — either there are no contacts available, or you've already emailed all of them before. Please choose different brands and try again.`,
                confirmText: "Try Again",
                confirm: () => {
                  handleClose();
                  setConfirmDialogData(null);
                },
              });
              return;
            }
          }

          if (jsonMessage.field === "date") {
            if (templates.length >= 5) {
              setTemplatesError({
                idx: 4,
                message:
                  "We are not able to schedule emails so far in advance. Please reduce your number of follow-ups",
              });
            } else {
              setBrandsError(
                `Unfortunately we cannot schedule so many emails at once. Can you reduce the number of brands. The ideal number is less than 200 brands. If you continue to face this error, please contact us at hello@onbento.com.`,
              );
            }
            return;
          }

          if (jsonMessage.field === "paywall") {
            if (jsonMessage.message.includes("remaining this month")) {
              setUpgradeDialogSource(UpgradeDialogSource.hits_limit);
            } else if (jsonMessage.message.includes("emails at once")) {
              setUpgradeDialogSource(UpgradeDialogSource.hits_campaign_limit);
            } else {
              setUpgradeDialogSource(UpgradeDialogSource.campaigns_limit);
            }
            return;
          }
        } else if (jsonMessage.message?.includes("Google integration")) {
          setOpenIntegrationDialog(true);
          return;
        }
        setAlert(jsonMessage.message, "error");
      } else {
        openCompleted(jsonMessage.campaign.id);
        handleClose();
      }
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setNextLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isMobileScreen}
      maxWidth="lg"
    >
      <DialogTitle>
        <Typography variant="h4">Pitch to Brands</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Typography
              variant="h6"
              gutterBottom
              display="flex"
              alignItems="center"
              gap={1}
            >
              Brands{" "}
              <Typography
                component="span"
                color="textSecondary"
                variant="caption"
              >
                (Processed{" "}
                {
                  brandVerifications.filter(
                    (bV) => bV.status !== BrandStatus.loading,
                  ).length
                }
                /{googlePlaceBrands.length})
              </Typography>
            </Typography>
            {!!brandsError && (
              <Typography paragraph color="error" variant="caption">
                {brandsError}
              </Typography>
            )}
            <Box sx={{ maxHeight: "60vh", overflowY: "auto" }}>
              {brandVerifications.map((brandData) => (
                <PendingItem
                  key={brandData.googlePlaceBrand.id}
                  brandData={brandData}
                  onDelete={removeGooglePlaceBrand}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography gutterBottom variant="h6">
              Set your Templates
            </Typography>
            <Typography color="textSecondary">
              Select the templates you want to use for pitching to these brands.
            </Typography>
            <Grid container sx={{ my: 2 }} columnSpacing={0.5}>
              <Grid item xs={12} sm={3}>
                <Typography sx={{ mt: 0.75 }}>Outreach Template</Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                {templates[0] ? (
                  <OutreachSelect
                    templates={templates}
                    handleChange={handleTemplateChange}
                    filteredOutreachTemplates={filteredOutreachTemplates}
                    templateFromEmailSettings={templateFromEmailSettings}
                  />
                ) : (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGenerateTemplate}
                    sx={{ height: 25, mt: 0.4, textTransform: "none" }}
                    disableElevation
                  >
                    Generate Template
                  </Button>
                )}
                {templatesError && templatesError.idx === 0 && (
                  <Grid container>
                    <Typography
                      variant="caption"
                      sx={{ fontStyle: "italic", color: "error.main" }}
                    >
                      {templatesError.message}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid container sx={{ mb: 2 }} columnSpacing={{ xs: 0, sm: 0.5 }}>
              <Grid item xs={12} sm={3}>
                <Typography
                  sx={{ mt: selectedFollowups.length > 0 ? 0.75 : 0.5 }}
                >
                  Follow-up Templates
                </Typography>
              </Grid>
              <Grid item xs={12} sm={9}>
                {selectedFollowups?.map((followup, index) => (
                  <Grid container sx={{ mb: 1 }} key={index}>
                    <Grid container alignItems="center">
                      <Select
                        onChange={(e) => handleTemplateChange(e, index + 1)}
                        value={followup?.id}
                        size="small"
                      >
                        {filteredFollowUps?.map((template) => (
                          <MenuItem key={template.id} value={template.id}>
                            <Box>
                              {template.name}
                              {template.id &&
                                templateFromEmailSettings?.includes(
                                  template.id,
                                ) && (
                                  <Chip
                                    label="Last Used"
                                    size="small"
                                    sx={{
                                      ml: 1,
                                      fontSize: 10,
                                      backgroundColor: "primary.light",
                                    }}
                                  />
                                )}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>

                      {followup?.id && (
                        <TemplateEditButton
                          sx={{ ml: 1 }}
                          templateId={followup.id}
                        />
                      )}

                      <IconButton
                        sx={{ fontSize: 13, color: grey[500] }}
                        onClick={() => removeFollowUp(index + 1)}
                      >
                        <Box component="i" className="fa-regular fa-trash" />
                      </IconButton>
                    </Grid>
                    {templatesError && templatesError.idx === index + 1 && (
                      <Grid container>
                        <Typography
                          variant="caption"
                          sx={{ fontStyle: "italic", color: "error.main" }}
                        >
                          {templatesError.message}
                        </Typography>
                      </Grid>
                    )}
                    <Grid container>
                      <Typography
                        variant="caption"
                        sx={{ fontStyle: "italic", fontSize: 10 }}
                        color="textSecondary"
                      >
                        Sent 4 business days after previous email
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
                {followupTemplates?.length > 0 &&
                  templates?.length < MAXIMUM_FOLLOWUP_ALLOWED + 1 && (
                    <Grid container alignItems="center">
                      <Grid item xs md>
                        <Button
                          disableElevation
                          onClick={addFollowUp}
                          sx={{
                            fontSize: 12,
                            textTransform: "none",
                            px: 0,
                          }}
                        >
                          + Follow Up
                        </Button>
                      </Grid>
                    </Grid>
                  )}
                {followupTemplates.length === 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleGenerateTemplate}
                    sx={{
                      height: 25,
                      mt: 0.4,
                      textTransform: "none",
                    }}
                    disableElevation
                  >
                    Generate Follow-up
                  </Button>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={handleBack}>
          Back
        </Button>
        <LoadingButton
          loading={nextLoading}
          color="primary"
          variant="contained"
          disableElevation
          onClick={() => {
            handleCreateCollection(false);
          }}
        >
          Continue
        </LoadingButton>
      </DialogActions>

      {confirmDialogData && (
        <ConfirmDialog
          header={confirmDialogData.header}
          subheader={confirmDialogData.subheader}
          confirmText={confirmDialogData.confirmText}
          open={!!confirmDialogData}
          cancelText={confirmDialogData.cancelText}
          handleCancel={confirmDialogData.cancel}
          handleConfirm={confirmDialogData.confirm}
        />
      )}
    </Dialog>
  );
}
